import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

import useBulkApproveRejectFlows from 'modules/approvals/hooks/useBulkApproveRejectFlows'
import type { RecommendationAction } from 'modules/timeoff/components/LeaveApprovalRecommendation/RecommendationAction'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import { trackApprovalRecommendation } from 'modules/timeoff/lib/tracking'
import type Leave from 'modules/timeoff/store/models/Leave'

import { useApprovalFlowStepsCount } from './useApprovalFlowStepsCount'

interface AcceptButtonProps {
  leave: Leave
  form: LeaveForm
  onFetchStats: () => void
  canApprove: boolean
  canUpdate: boolean
  isCurrentApprover?: boolean
  materializedApprovalFlowId?: string
  isCurrentApproverLoading?: boolean
  approvalFlowId?: string
  disabled: boolean
  fromShifts?: boolean
  onClose: () => void
  onlySubmit?: boolean
  employeeId: string
  recommendationAction?: RecommendationAction
  canApproveFromFlow?: boolean
  hasPendingChanges?: boolean
  shouldSaveLeaveBeforeApprove?: boolean
  onBeforeApprove?: () => Promise<boolean>
}

const AcceptButton = ({
  leave,
  form,
  onFetchStats,
  canApprove,
  canUpdate,
  isCurrentApprover,
  materializedApprovalFlowId,
  isCurrentApproverLoading,
  approvalFlowId,
  disabled,
  fromShifts = false,
  onClose,
  onlySubmit = false,
  employeeId,
  recommendationAction,
  canApproveFromFlow,
  hasPendingChanges,
  shouldSaveLeaveBeforeApprove,
  onBeforeApprove,
}: AcceptButtonProps) => {
  const { closePortal } = usePortal()
  const { fetchData } = useLeaves()
  const { access } = useCurrentSession()
  const { approve: approveFlow } = useBulkApproveRejectFlows()

  const { loading: stepApproversCountLoading, stepsCount } =
    useApprovalFlowStepsCount(approvalFlowId)

  const isOneStepApprovalFlow = stepsCount === 1

  const loading = isCurrentApproverLoading || stepApproversCountLoading

  const submitButton = (
    <Button disabled={disabled} submit>
      {i18n.t('time_off.admin.edit_approved.submit')}
    </Button>
  )

  // Use canApproveFromFlow from backend if available, as it includes proper permission checks
  // (including the 'owned' context that prevents self-approval when ApproveLeaves has owned: false)
  // Falls back to frontend logic for backward compatibility when canApproveFromFlow is undefined
  const canShowApproveButton =
    canApproveFromFlow !== undefined ? canApproveFromFlow : canApprove || isCurrentApprover

  if (loading || leave.isApproved() || leave.isRejected() || !canShowApproveButton) {
    return submitButton
  }

  const runPostApproveSideEffects = async (action: string) => {
    onFetchStats()
    await fetchData(employeeId)
    closePortal()
    onClose()
    track(trackingEvents.timeoff.reviewTimeOffRequestSubmit, {
      request_action: action,
      from_shifts: fromShifts,
    })

    if (recommendationAction) {
      trackApprovalRecommendation('approve', recommendationAction)
    }
  }

  const handleSuccess = async (action: string, value: Leave) => {
    leave.set(value)
    await runPostApproveSideEffects(action)
  }

  const performAction = async (action: string, rpcMethod: string) => {
    // Guard: don't attempt to approve an already-approved leave
    if (leave.isApproved()) return

    const shouldSaveBeforeApprove = hasPendingChanges ?? form.isDirty

    if (canUpdate && shouldSaveBeforeApprove && (shouldSaveLeaveBeforeApprove ?? form.isDirty)) {
      const { success } = await asyncAction(() => form.save(leave))

      if (!success) {
        return
      }
    }

    if (onBeforeApprove) {
      const canContinue = await onBeforeApprove()

      if (!canContinue) {
        return
      }
    }

    if (materializedApprovalFlowId) {
      const updateWholeFlow = rpcMethod === 'approve_all'

      await approveFlow(
        {
          accessId: access.id,
          materializedFlowId: materializedApprovalFlowId,
          updateWholeFlow,
        },
        async () => runPostApproveSideEffects(action)
      )
      return
    }

    const { success, value } = await asyncAction(() => leave.rpc(rpcMethod))

    if (success) handleSuccess(action, value)
  }

  const approveStep = async () => {
    await performAction('approve', 'approve')
  }

  const approveEntireRequest = async () => {
    await performAction('approve_all', 'approve_all')
  }

  const approveStepTitle = i18n.t('time_off.admin.review.approve_options.step')
  const approveGlobalTitle = i18n.t('time_off.admin.review.approve_options.global')
  const defaultApproveActionTitle = isCurrentApprover ? approveStepTitle : approveGlobalTitle
  const approveTitle = isOneStepApprovalFlow ? i18n.t('common.approve') : defaultApproveActionTitle

  const defaultAction = isCurrentApprover ? approveStep : approveEntireRequest
  const isRecommendationWithShifts = onlySubmit

  if (isRecommendationWithShifts) {
    return (
      <Button type="positive" submit>
        {approveTitle}
      </Button>
    )
  }

  if (isOneStepApprovalFlow || !canApprove) {
    return (
      <Button type="positive" onClick={defaultAction}>
        {approveTitle}
      </Button>
    )
  }

  return (
    <Button.Split label={defaultApproveActionTitle} type="positive" onClick={defaultAction}>
      <Button.Split.Option disabled={!isCurrentApprover} onClick={approveStep}>
        {approveStepTitle}
      </Button.Split.Option>
      <Button.Split.Option onClick={approveEntireRequest} disabled={!canApprove}>
        {approveGlobalTitle}
      </Button.Split.Option>
    </Button.Split>
  )
}

export default observer(AcceptButton)
